import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Input, Select, DatePicker, Card, Popconfirm, Tag } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  SendOutlined,
  CloseCircleOutlined,
  UndoOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import ClaimStatusTag from '../components/ClaimStatusTag';
import ClaimFormModal from '../components/ClaimFormModal';
import ClaimDetailDrawer from '../components/ClaimDetailDrawer';
import {
  getMyClaims,
  createClaim,
  updateClaim,
  submitClaim,
  cancelClaim,
  fetchMasterData,
  getClaimDetail,
} from '../costsClaimAction';
import costsClaimApi from 'api/costsClaimApi';

const { Option } = Select;
const { RangePicker } = DatePicker;

export const MyClaimsList: React.FC = () => {
  const dispatch = useDispatch();
  const { myClaims, loading, total, expenseTypes, paymentMethods, banks, currentClaim } =
    useSelector((state: any) => state.costsClaim || {});

  const [searchKw, setSearchKw] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<any>(null);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [editingClaim, setEditingClaim] = useState<any>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    dispatch(fetchMasterData() as any);
    loadData();
  }, [dispatch]);

  const loadData = (page = 1, kw = searchKw, st = statusFilter, dr = dateRange) => {
    const params: any = { page, perPage: 20 };
    if (kw) params.keyword = kw;
    if (st) params.status = st;
    if (dr && dr[0] && dr[1]) {
      params.fromDate = dr[0].format('YYYY-MM-DD');
      params.toDate = dr[1].format('YYYY-MM-DD');
    }
    dispatch(getMyClaims(params) as any);
  };

  const handleResetFilters = () => {
    setSearchKw('');
    setStatusFilter(undefined);
    setDateRange(null);
    loadData(1, '', undefined, null);
  };

  const handleCreateOrUpdate = async (values: any, fileList: any[]) => {
    if (editingClaim) {
      await dispatch(updateClaim({ id: editingClaim.id, body: values }) as any);
    } else {
      const res: any = await dispatch(createClaim(values) as any);
      const newClaimId = res?.payload?.data?.id;
      if (newClaimId && fileList.length > 0) {
        for (const file of fileList) {
          const formData = new FormData();
          formData.append('file', file);
          await costsClaimApi.uploadAttachment(newClaimId, formData);
        }
      }
    }
    setFormModalVisible(false);
    setEditingClaim(null);
    loadData();
  };

  const handleSubmitClaim = async (id: number) => {
    await dispatch(submitClaim(id) as any);
    loadData();
  };

  const handleCancelClaim = async (id: number) => {
    await dispatch(cancelClaim(id) as any);
    loadData();
  };

  const handleViewDetail = (record: any) => {
    dispatch(getClaimDetail(record.id) as any);
    setDrawerVisible(true);
  };

  const columns = [
    {
      title: 'Mã Claim',
      dataIndex: 'claimCode',
      key: 'claimCode',
      render: (text: string, record: any) => (
        <Button type="link" onClick={() => handleViewDetail(record)} style={{ padding: 0 }}>
          {text}
        </Button>
      ),
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      render: (val: number, record: any) => (
        <span style={{ fontWeight: '600', color: '#1890ff' }}>
          {val ? `${Number(val).toLocaleString('vi-VN')} ${record.currency || 'VND'}` : '0 VND'}
        </span>
      ),
    },
    {
      title: 'Ngày phát sinh',
      dataIndex: 'claimDate',
      key: 'claimDate',
      render: (d: string) => (d ? moment(d).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (st: string) => <ClaimStatusTag status={st} />,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 140,
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined style={{ color: '#1890ff' }} />}
            onClick={() => handleViewDetail(record)}
            title="Xem chi tiết"
          />

          {record.status === 'DRAFT' && (
            <>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined style={{ color: '#fa8c16' }} />}
                onClick={() => {
                  setEditingClaim(record);
                  setFormModalVisible(true);
                }}
                title="Sửa"
              />

              <Popconfirm
                title="Bạn có chắc chắn muốn nộp claim này?"
                onConfirm={() => handleSubmitClaim(record.id)}
                okText="Nộp"
                cancelText="Hủy"
              >
                <Button
                  type="text"
                  size="small"
                  icon={<SendOutlined style={{ color: '#52c41a' }} />}
                  title="Gửi duyệt"
                />
              </Popconfirm>
            </>
          )}

          {['DRAFT', 'SUBMITTED'].includes(record.status) && (
            <Popconfirm
              title="Xác nhận hủy yêu cầu claim này?"
              onConfirm={() => handleCancelClaim(record.id)}
              okText="Hủy Claim"
              cancelText="Quay lại"
            >
              <Button type="text" size="small" danger icon={<CloseCircleOutlined />} title="Hủy" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Danh sách Claim Chi Phí của Tôi"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingClaim(null);
              setFormModalVisible(true);
            }}
          >
            Tạo Claim Mới
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="Tìm theo mã / tiêu đề..."
            prefix={<SearchOutlined />}
            allowClear
            value={searchKw}
            onChange={(e) => {
              const val = e.target.value;
              setSearchKw(val);
              if (!val) {
                loadData(1, '', statusFilter, dateRange);
              }
            }}
            onPressEnter={() => loadData(1, searchKw, statusFilter, dateRange)}
            style={{ width: 220 }}
          />

          <Select
            placeholder="Trạng thái"
            allowClear
            value={statusFilter}
            onChange={(val) => {
              setStatusFilter(val);
              loadData(1, searchKw, val, dateRange);
            }}
            style={{ width: 180 }}
          >
            <Option value="DRAFT">Nháp</Option>
            <Option value="SUBMITTED">Chờ L1 duyệt</Option>
            <Option value="APPROVED_BY_APPROVER">Chờ Finance duyệt</Option>
            <Option value="APPROVED_BY_FINANCE">Chờ thanh toán</Option>
            <Option value="PAID">Đã thanh toán</Option>
            <Option value="REJECTED_BY_APPROVER">L1 Từ chối</Option>
            <Option value="REJECTED_BY_FINANCE">Finance Từ chối</Option>
            <Option value="CANCELLED">Đã hủy</Option>
          </Select>

          <RangePicker
            format="DD/MM/YYYY"
            value={dateRange}
            onChange={(dates) => {
              setDateRange(dates);
              loadData(1, searchKw, statusFilter, dates);
            }}
            placeholder={['Từ ngày', 'Đến ngày']}
          />

          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => loadData(1, searchKw, statusFilter, dateRange)}
          >
            Tìm kiếm
          </Button>

          {(searchKw || statusFilter || dateRange) && (
            <Button icon={<UndoOutlined />} onClick={handleResetFilters}>
              Đặt lại
            </Button>
          )}
        </Space>

        <Table
          columns={columns}
          dataSource={myClaims}
          rowKey="id"
          loading={loading}
          pagination={{
            total,
            pageSize: 20,
            onChange: (p) => loadData(p),
          }}
        />
      </Card>

      <ClaimFormModal
        visible={formModalVisible}
        onCancel={() => {
          setFormModalVisible(false);
          setEditingClaim(null);
        }}
        onSubmit={handleCreateOrUpdate}
        initialValues={editingClaim}
        expenseTypes={expenseTypes}
        paymentMethods={paymentMethods}
        banks={banks}
        loading={loading}
      />

      <ClaimDetailDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        claim={currentClaim}
      />
    </div>
  );
};

export default MyClaimsList;
